import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Sparkle, Copy, Phone, Users as UsersIcon, Note, EnvelopeSimple,
  ArrowRight, Star, ClockCounterClockwise, PlusCircle,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/crm`;

const STAGE_KEYS = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];

const scoreColor = (score) => {
  if (score == null) return '#6B7280';
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#A855F7';
  if (score >= 40) return '#F59E0B';
  return '#EF4444';
};

const ACT_ICONS = {
  created: PlusCircle,
  stage_change: ArrowRight,
  score: Star,
  note: Note,
  call: Phone,
  meeting: UsersIcon,
  email: EnvelopeSimple,
  ai_email: Sparkle,
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) + ' ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const inputCls = 'w-full px-3 py-2 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#2563EB] focus:outline-none';
const labelCls = 'text-xs uppercase tracking-[0.15em] text-white/50 font-semibold mb-1.5 block';


const ActivityTimeline = ({ contactId, t, refreshKey }) => {
  const [activities, setActivities] = useState([]);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('note');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contacts/${contactId}/activities`, { withCredentials: true });
      setActivities(res.data.activities || []);
    } catch { /* silent */ }
  }, [contactId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const addActivity = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/contacts/${contactId}/activities`, { kind, text: text.trim() }, { withCredentials: true });
      setText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10" data-testid="activity-timeline">
      <div className="flex items-center gap-2 mb-3">
        <ClockCounterClockwise size={14} weight="bold" className="text-white/50" />
        <span className={labelCls} style={{ marginBottom: 0 }}>{t('crm.timeline')}</span>
      </div>

      <div className="flex gap-2 mb-3">
        <select
          data-testid="activity-kind-select"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="px-2 py-2 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-xs focus:border-[#2563EB] focus:outline-none"
        >
          {['note', 'call', 'meeting', 'email'].map((k) => (
            <option key={k} value={k}>{t(`crm.kind_${k}`)}</option>
          ))}
        </select>
        <input
          data-testid="activity-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addActivity(); } }}
          placeholder={t('crm.activity_ph')}
          className={inputCls + ' flex-1'}
        />
        <button
          onClick={addActivity}
          disabled={adding || !text.trim()}
          data-testid="activity-add-btn"
          className="px-3 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-all"
        >
          {t('crm.add_activity')}
        </button>
      </div>

      {activities.length === 0 ? (
        <div className="text-xs text-white/40 py-2" data-testid="no-activity">{t('crm.no_activity')}</div>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="activity-list">
          {activities.map((a) => {
            const Icon = ACT_ICONS[a.kind] || Note;
            return (
              <div key={a.activity_id} className="flex items-start gap-2.5 text-xs" data-testid={`activity-${a.activity_id}`}>
                <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon size={11} weight="bold" className="text-white/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-white/80 font-semibold">{t(`crm.act_${a.kind}`)}</span>
                  {a.text && <span className="text-white/55"> · {a.text}</span>}
                  <div className="text-[10px] text-white/35 mt-0.5">{fmtDateTime(a.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const AIEmailPanel = ({ contactId, t, onGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [email, setEmail] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setEmail(null);
    try {
      const res = await axios.post(`${API}/contacts/${contactId}/followup-email`, {}, { withCredentials: true });
      setEmail(res.data);
      onGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('crm.ai_email_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(`${email.subject}\n\n${email.body}`)
      .then(() => toast.success(t('crm.ai_email_copied')))
      .catch(() => {});
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <button
        onClick={generate}
        disabled={generating}
        data-testid="ai-email-btn"
        className="w-full px-4 py-2.5 bg-[#A855F7]/15 hover:bg-[#A855F7]/25 border border-[#A855F7]/30 text-[#C084FC] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
      >
        <Sparkle size={15} weight="bold" />
        {generating ? t('crm.ai_email_generating') : t('crm.ai_email')}
      </button>

      {email && (
        <div className="mt-3 p-3 bg-[#0F0F0F] border border-[#A855F7]/25 rounded-lg" data-testid="ai-email-result">
          <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-1">
            {t('crm.ai_email_subject')}
          </div>
          <div className="text-sm text-white font-semibold mb-2" data-testid="ai-email-subject">{email.subject}</div>
          <div className="text-xs text-white/70 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto" data-testid="ai-email-body">
            {email.body}
          </div>
          <button
            onClick={copyEmail}
            data-testid="ai-email-copy-btn"
            className="mt-3 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all"
          >
            <Copy size={12} weight="bold" />
            {t('crm.ai_email_copy')}
          </button>
        </div>
      )}
    </div>
  );
};


export const CRMContactModal = ({ contact, isOpen, onClose, onSave, t }) => {
  const [form, setForm] = useState({
    name: '', email: '', company: '', stage: 'new', tags: [], notes: '',
    value: '', next_action_at: '', next_action_note: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: contact?.name || '',
        email: contact?.email || '',
        company: contact?.company || '',
        stage: contact?.stage || 'new',
        tags: contact?.tags || [],
        notes: contact?.notes || '',
        value: contact?.value != null ? String(contact.value) : '',
        next_action_at: contact?.next_action_at || '',
        next_action_note: contact?.next_action_note || '',
      });
      setTagInput('');
    }
  }, [contact, isOpen]);

  const addTag = () => {
    const t2 = tagInput.trim().toLowerCase();
    if (t2 && !form.tags.includes(t2) && form.tags.length < 8) {
      setForm({ ...form, tags: [...form.tags, t2] });
    }
    setTagInput('');
  };

  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter((x) => x !== tag) });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('crm.name') + ' *');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        stage: form.stage,
        tags: form.tags,
        notes: form.notes,
      };
      if (form.email) payload.email = form.email;
      if (form.company) payload.company = form.company;
      if (form.value !== '' && !isNaN(Number(form.value))) {
        payload.value = Math.max(0, Number(form.value));
      } else if (contact) {
        payload.value = 0;
      }
      if (form.next_action_at) {
        payload.next_action_at = form.next_action_at;
        payload.next_action_note = form.next_action_note || '';
      } else if (contact?.next_action_at) {
        payload.clear_next_action = true;
      }
      await onSave(payload, contact?.contact_id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="contact-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-white/15 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" data-testid="contact-modal-title">
            {contact ? t('crm.edit') : t('crm.new_contact')}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="contact-modal-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>{t('crm.name')} *</label>
            <input
              data-testid="input-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('crm.email')}</label>
              <input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('crm.company')}</label>
              <input
                data-testid="input-company"
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('crm.stage')}</label>
              <select
                data-testid="select-stage"
                value={form.stage}
                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                className={inputCls}
              >
                {STAGE_KEYS.map((s) => (
                  <option key={s} value={s}>{t(`crm.stage_${s}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t('crm.value_label')}</label>
              <input
                data-testid="input-value"
                type="number"
                min="0"
                step="1"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="0"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('crm.next_action')}</label>
              <input
                data-testid="input-next-action-date"
                type="date"
                value={form.next_action_at}
                onChange={(e) => setForm({ ...form, next_action_at: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>&nbsp;</label>
              <input
                data-testid="input-next-action-note"
                type="text"
                value={form.next_action_note}
                onChange={(e) => setForm({ ...form, next_action_note: e.target.value })}
                placeholder={t('crm.next_action_note_ph')}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>{t('crm.tags')}</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  data-testid={`tag-chip-${tag}`}
                  className="text-xs px-2 py-0.5 rounded bg-[#2563EB]/20 text-[#60A5FA] border border-[#2563EB]/30 inline-flex items-center gap-1"
                >
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-white" aria-label="remove-tag">
                    <X size={10} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
            <input
              data-testid="input-tag"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder={t('crm.tags_hint')}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>{t('crm.notes')}</label>
            <textarea
              data-testid="input-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder={t('crm.notes_placeholder')}
              className={inputCls + ' resize-none'}
            />
          </div>

          {contact?.score != null && (
            <div className="p-3 bg-[#0F0F0F] border border-white/10 rounded-lg" data-testid="score-display">
              <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-1">
                {t('crm.score_reasoning')}
              </div>
              <div className="flex items-start gap-3">
                <div className="text-2xl font-bold shrink-0" style={{ color: scoreColor(contact.score) }}>
                  {contact.score}
                </div>
                <div className="text-xs text-white/70 leading-relaxed">{contact.score_reasoning || '—'}</div>
              </div>
            </div>
          )}
        </div>

        {contact && (
          <>
            <AIEmailPanel contactId={contact.contact_id} t={t} onGenerated={() => setTimelineKey((k) => k + 1)} />
            <ActivityTimeline contactId={contact.contact_id} t={t} refreshKey={timelineKey} />
          </>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/70 hover:text-white"
            data-testid="contact-modal-cancel"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={saving || !form.name.trim()}
            data-testid="contact-modal-save"
            className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] disabled:bg-white/10 disabled:text-white/40 text-white text-sm font-semibold rounded-lg transition-all"
          >
            {saving ? t('common.saving') : t('crm.save')}
          </button>
        </div>
      </div>
    </div>
  );
};
